import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Disclosure,
  Notice,
  Segmented,
  Skeleton,
  TextField,
  StateScreen,
  iconCopy,
  useSnackbar,
} from '@sauda/ui/kit';
import { Screen } from '../app/chrome.js';
import { useAuth } from '../auth/AuthContext.js';
import { api } from '../lib/api.js';
import {
  MESSAGE_LANGS,
  askClientMessage,
  askOwnerMessage,
  proofMessage,
  type MessageLang,
} from '../lib/proof-message.js';
import { whatsappLink } from '../lib/share-text.js';
import { formatIst, formatIstDate } from '../lib/time.js';
import type { IntroductionLinks, ProofView } from '../lib/types.js';

/** The introduction proof in words, to send to an owner or a client, with the record itself under Details. */
export function ProofScreen() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const auth = useAuth();
  const snack = useSnackbar();
  const cache = useQueryClient();
  const [lang, setLang] = useState<MessageLang>('en');
  const [months, setMonths] = useState('6');
  const proof = useQuery({
    queryKey: ['proof', id],
    queryFn: () => api<ProofView>(`/introductions/${id}`),
    enabled: auth.status === 'authenticated',
  });
  const links = useQuery({
    queryKey: ['proof-links', id],
    queryFn: () => api<IntroductionLinks>(`/introductions/${id}/links`),
    enabled: auth.status === 'authenticated',
  });
  const make = useMutation({
    mutationFn: () => {
      const n = Number(months.trim());
      const periodMonths = months.trim() === '' || !Number.isInteger(n) ? null : n;
      return api<IntroductionLinks>(`/introductions/${id}/links`, {
        method: 'POST',
        body: { periodMonths },
      });
    },
    onSuccess: async (made) => {
      cache.setQueryData(['proof-links', id], made);
      await cache.invalidateQueries({ queryKey: ['proof', id] });
    },
    onError: () => snack({ text: t('proof.linksFailed') }),
  });
  const data = proof.data;
  if (proof.isError)
    return (
      <Screen title={t('proof.title')} back>
        <StateScreen kind="empty" compact title={t('proof.notFound')} />
      </Screen>
    );
  if (data === undefined)
    return (
      <Screen title={t('proof.title')} back>
        <Skeleton height={120} />
      </Screen>
    );
  const copy = (value: string) => {
    void navigator.clipboard?.writeText(value);
    snack({ text: t('proof.copied') });
  };
  const flat = data.property.label ?? data.property.title ?? '';
  const client = data.client.name ?? t('proof.aClient');
  const broker = data.listedBy ?? auth.name ?? data.agencyName;
  const facts = { agency: data.agencyName, broker, client, property: flat };
  const date = formatIstDate(data.record.createdAt);
  const have = links.data;
  const ack = (iso: string | null | undefined) =>
    iso === null || iso === undefined
      ? t('proof.notYet')
      : t('proof.doneOn', { date: formatIst(iso) });
  return (
    <Screen title={t('proof.title')} back eyebrow={flat}>
      <Card hero>
        <p className="k-t-title" data-testid="proof-story">
          {t('proof.story', { client, property: flat, broker, date })}
        </p>
        <ul className="proof-lines">
          {data.verification.by.map((e) => (
            <li key={e.kind + e.at}>
              {t(`proof.kind_${e.kind}`)}, {formatIst(e.at)}
            </li>
          ))}
          <li>
            {data.ownerToldAt === null
              ? t('proof.ownerNotTold')
              : t('proof.ownerTold', { date: formatIst(data.ownerToldAt) })}
          </li>
        </ul>
      </Card>
      {data.verification.verified ? null : <Notice tone="info">{t('proof.unverifiedHelp')}</Notice>}

      <Card>
        <h2 className="k-t-title-sm">{t('proof.send')}</h2>
        <Segmented
          label={t('proof.language')}
          value={lang}
          onChange={(v) => setLang(v as MessageLang)}
          options={MESSAGE_LANGS}
        />
        {have?.share == null ? (
          <p className="k-t-body k-muted">{t('proof.linksFirst')}</p>
        ) : (
          <div className="chips-row">
            <a
              className="k-btn k-btn--filled k-btn--lg k-layer k-t-label-lg"
              href={whatsappLink(
                proofMessage(lang, { ...facts, date, link: have.share.link }),
                data.owner.phone,
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('proof.sendOwner')}
            </a>
            <a
              className="k-btn k-btn--tonal k-btn--lg k-layer k-t-label-lg"
              href={`${have.share.link}?lang=${lang}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('proof.openPage')}
            </a>
          </div>
        )}
      </Card>

      <Card>
        <h2 className="k-t-title-sm">{t('proof.ackTitle')}</h2>
        <p className="k-t-body k-muted">{t('proof.ackHelp')}</p>
        <TextField
          label={t('proof.period')}
          inputMode="numeric"
          value={months}
          onChange={(e) => setMonths(e.target.value)}
          supporting={t('proof.periodHint')}
        />
        <Button
          variant="tonal"
          loading={make.isPending}
          disabled={auth.status !== 'authenticated'}
          onClick={() => make.mutate()}
        >
          {have?.owner == null ? t('proof.makeLinks') : t('proof.saveTerm')}
        </Button>
        {have?.owner == null || have.client == null ? null : (
          <>
            <div className="deal-client__row">
              <div className="row-main k-grow">
                <p className="k-t-title-sm">{t('proof.ownerLine')}</p>
                <p className="k-t-body-sm k-muted">{ack(have.owner.acknowledgedAt)}</p>
              </div>
              <a
                className="k-btn k-btn--tonal k-btn--md k-layer k-t-label-lg"
                href={whatsappLink(
                  askOwnerMessage(lang, { ...facts, link: have.owner.link }),
                  data.owner.phone,
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('proof.askOwner')}
              </a>
            </div>
            <div className="deal-client__row">
              <div className="row-main k-grow">
                <p className="k-t-title-sm">{t('proof.clientLine')}</p>
                <p className="k-t-body-sm k-muted">{ack(have.client.acknowledgedAt)}</p>
              </div>
              <a
                className="k-btn k-btn--tonal k-btn--md k-layer k-t-label-lg"
                href={whatsappLink(
                  askClientMessage(lang, { ...facts, link: have.client.link }),
                  data.client.phone,
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('proof.askClient')}
              </a>
            </div>
          </>
        )}
      </Card>

      <Disclosure summary={t('proof.details')}>
        <p className="k-t-body-sm" data-testid="chain-position">
          {t('proof.position', { seq: data.position.seq, total: data.position.total })}
        </p>
        {data.chain.verified ? (
          <p className="k-t-body-sm" data-testid="chain-status">
            {t('proof.chainVerified', { n: data.position.seq })}
          </p>
        ) : (
          <Notice tone="error">
            <span data-testid="chain-status">
              {t('proof.chainBroken', { n: data.chain.atSeq })}
            </span>
          </Notice>
        )}
        <p className="k-t-body-sm" data-testid="verification-status">
          {data.verification.verified ? t('proof.verified') : t('proof.unverified')}
        </p>
        <p className="k-t-label k-muted">{t('proof.hash')}</p>
        <p className="k-t-mono hash">{data.record.hash}</p>
        <p className="k-t-label k-muted">{t('proof.prevHash')}</p>
        <p className="k-t-mono hash">{data.record.prevHash}</p>
        <div>
          <Button variant="tonal" icon={iconCopy} onClick={() => copy(data.record.hash)}>
            {t('proof.copyHash')}
          </Button>
        </div>
      </Disclosure>
    </Screen>
  );
}
